import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { graphqlMutation } from '../utils/graphqlApi';
import { generateEvalsReport } from '../graphql/mutations';
import useUserPermissions from '../hooks/useUserPermissions';
import './AiGovernance.css';

// ── Defaults & helpers ──────────────────────────────────────────────────
const DEFAULT_WINDOWS = [
  { value: '24h', label: '24 Hours' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
];

const RAG_GREEN = 0.9;
const RAG_AMBER = 0.75;

const parseJsonSafely = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (parseError) {
    console.error('[AiGovernance] Failed to parse JSON payload:', parseError);
    return null;
  }
};

const summarizeReport = (data) => {
  try {
    const windows = {};
    Object.entries(data?.windows || {}).forEach(([wkey, wval]) => {
      const s = wval?.summary || {};
      windows[wkey] = {
        total_agents: s.total_agents ?? null,
        agents_with_evals: s.agents_with_evals ?? null,
        agent_table_rows: (s.agent_table || []).length,
        total_cost: s.cost?.total_cost ?? null,
        records: s.records ?? null,
        sessions: s.sessions ?? null,
      };
    });
    return { meta_windows: Object.keys(data?.meta?.windows || {}).length, windows };
  } catch (e) {
    return { summary_error: String(e) };
  }
};

// ── Formatters (matching the reference HTML) ────────────────────────────
const pct = (v, d = 1) => {
  if (v == null || !Number.isFinite(Number(v))) return 'N/A';
  return `${(Number(v) * 100).toFixed(d)}%`;
};

const usd = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return 'N/A';
  const n = Number(v);
  if (n >= 100) return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(4)}`;
};

const tokfmt = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return 'N/A';
  const n = Number(v);
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
};

const fmtRisk = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return 'N/A';
  return Number(v).toFixed(2);
};

const fmtPattern = (v) => {
  if (!v) return 'N/A';
  return String(v)
    .toLowerCase()
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' '); // SUPERVISOR_AGENT -> "Supervisor Agent"
};

// ── RAG helpers (same thresholds + colors as reference HTML) ─────────────
const ragClass = (v) => {
  if (v == null) return 'ge-muted';
  if (v >= RAG_GREEN) return 'ge-good';
  if (v >= RAG_AMBER) return 'ge-warn';
  return 'ge-bad';
};

const ragBg = (v) => {
  if (v == null) return '';
  if (v >= RAG_GREEN) return 'ge-bg-good';
  if (v >= RAG_AMBER) return 'ge-bg-warn';
  return 'ge-bg-bad';
};

// ── Sort accessors ──────────────────────────────────────────────────────
const SORT_ACCESSORS = {
  agent_name: (r) => (r.agent_name || '').toLowerCase(),
  agent_pattern: (r) => (r.agent_pattern || '').toLowerCase(),
  has_evals: (r) => (r.has_evals ? 1 : 0),
  accuracy: (r) => (r.accuracy == null ? -1 : r.accuracy),
  unique_users: (r) => (r.unique_users == null ? -1 : r.unique_users),
  avg_risk_score: (r) => (r.avg_risk_score == null ? -1 : r.avg_risk_score),
  scanned: (r) => (r.scanned == null ? -1 : r.scanned),
  tokens: (r) => (r.tokens == null ? -1 : r.tokens),
  cost: (r) => (r.cost == null ? -1 : r.cost),
};

function SortableHeader({ label, field, sortField, sortDir, onSort, className }) {
  const isActive = sortField === field;
  return (
    <th
      role="button"
      onClick={() => onSort(field)}
      className={className}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      {label}{' '}
      {isActive ? (
        <i className={`bi bi-caret-${sortDir === 'asc' ? 'up' : 'down'}-fill`} aria-hidden="true" />
      ) : (
        <i className="bi bi-caret-down" style={{ opacity: 0.25 }} aria-hidden="true" />
      )}
    </th>
  );
}

// ── KPI card (matching the reference's gradient + stripe) ───────────────
// Hint/subtext line intentionally omitted per request — cards show only the
// label and value now.
function KpiCard({ label, value, stripeScore, variant }) {
  const stripeClass = stripeScore != null ? ragBg(stripeScore) : '';
  const variantCls = variant === 'cost' ? 'ge-kpi-cost' : variant === 'users' ? 'ge-kpi-users' : '';
  return (
    <div className={`ge-kpi ${variantCls}`}>
      {stripeScore !== undefined && <span className={`ge-stripe ${stripeClass}`} />}
      <div className="ge-kpi-label">{label}</div>
      <div className="ge-kpi-value">{value}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Main component
// ═════════════════════════════════════════════════════════════════════════
function AiGovernance() {
  const navigate = useNavigate();
  const { canAccessAiGovernance, getUserRole, isLoading: isPermissionsLoading } = useUserPermissions();

  const [timeWindow, setTimeWindow] = useState('7d');
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [source, setSource] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);

  const [sortField, setSortField] = useState('cost');
  const [sortDir, setSortDir] = useState('desc');

  const requestRef = useRef(0);

  // ── Data fetch ──────────────────────────────────────────────────────
  const fetchReport = useCallback(async ({ forceRefresh = false } = {}) => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const isStale = () => requestRef.current !== requestId;

    setIsLoading(true);
    setError(null);

    const t0 = (typeof performance !== 'undefined' ? performance : Date).now();
    // eslint-disable-next-line no-console
    console.log(
      `[AiGovernance] generateEvalsReport → request (force_refresh=${forceRefresh}, requestId=${requestId})`
    );

    try {
      const result = await graphqlMutation(generateEvalsReport, {
        input: { force_refresh: forceRefresh },
      });
      if (isStale()) return;

      const rawResponse = result?.generateEvalsReport;
      const elapsedMs = Math.round((typeof performance !== 'undefined' ? performance : Date).now() - t0);
      // eslint-disable-next-line no-console
      console.log(
        `[AiGovernance] generateEvalsReport ← response in ${elapsedMs}ms (statusCode=${rawResponse?.statusCode ?? 'n/a'})`,
        result
      );
      const responseBody = rawResponse?.body;
      if (!responseBody) {
        // eslint-disable-next-line no-console
        console.error('[AiGovernance] No body on response:', rawResponse);
        setError('No report data returned.');
        return;
      }

      const parsed = parseJsonSafely(responseBody);
      if (!parsed) {
        // eslint-disable-next-line no-console
        console.error('[AiGovernance] Failed to parse body:', responseBody);
        setError('Failed to parse report response.');
        return;
      }

      // Handle double-encoded body (VTL double-serialization).
      const envelope = typeof parsed === 'string' ? parseJsonSafely(parsed) : parsed;
      if (!envelope || typeof envelope !== 'object') {
        // eslint-disable-next-line no-console
        console.error('[AiGovernance] Body is not an object after parsing:', typeof parsed, parsed);
        setError('Failed to parse report response.');
        return;
      }

      // Detect envelope vs raw report shape.
      const isEnvelope = typeof envelope.status === 'string';
      const isRawReport = !isEnvelope && envelope.meta && envelope.windows;

      // eslint-disable-next-line no-console
      console.log(
        `[AiGovernance] parsed body: isEnvelope=${isEnvelope}, isRawReport=${isRawReport}, ` +
          `status=${envelope.status}, source=${envelope.source}, hasData=${Boolean(envelope.data)}, ` +
          `hasMeta=${Boolean(envelope.meta)}, hasWindows=${Boolean(envelope.windows)}`
      );

      if (isEnvelope && (envelope.status === 'error' || !envelope.data)) {
        // eslint-disable-next-line no-console
        console.error('[AiGovernance] Error envelope:', rawResponse, envelope);
        setError(envelope.error_message || `Request failed (status ${rawResponse?.statusCode ?? 'unknown'}).`);
        return;
      }

      const rd = isEnvelope ? envelope.data : isRawReport ? envelope : null;
      if (!rd || !rd.windows) {
        // eslint-disable-next-line no-console
        console.error('[AiGovernance] Unrecognized payload shape:', rawResponse, envelope);
        setError('Unexpected report format — no windows data found.');
        return;
      }

      // eslint-disable-next-line no-console
      console.log('[AiGovernance] report shape:', summarizeReport(rd));

      setReportData(rd);
      setSource(isEnvelope ? envelope.source || null : null);
      setGeneratedAt(isEnvelope ? envelope.generated_at || null : rd.meta?.generated_at || null);
    } catch (err) {
      if (isStale()) return;
      console.error('[AiGovernance] Error fetching evals report:', err);
      setError(err.message || 'Unable to load GenAI evals report.');
    } finally {
      if (!isStale()) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isPermissionsLoading || !canAccessAiGovernance()) return;
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermissionsLoading, fetchReport]);

  // ── Derived data ────────────────────────────────────────────────────
  const meta = reportData?.meta || null;
  const windowLabels = meta?.windows || {};
  const windowOptions = DEFAULT_WINDOWS.map((w) => ({
    value: w.value,
    label: windowLabels[w.value] || w.label,
  }));

  const activeWindow = reportData?.windows?.[timeWindow] || null;
  const summary = activeWindow?.summary || null;
  // Merge in each agent's "sessions scanned" (guardrail scan count) from the
  // security breakdown so it can be shown as a column on the main table.
  const agentTable = useMemo(() => {
    const rows = summary?.agent_table || [];
    const scannedByAgent = {};
    (summary?.security?.agents || []).forEach((a) => {
      scannedByAgent[a.agent_id] = a.scanned;
    });
    return rows.map((r) => ({ ...r, scanned: scannedByAgent[r.agent_id] ?? null }));
  }, [summary]);
  const costData = summary?.cost || {};

  const agentsDeployed = summary?.total_agents ?? agentTable.length;
  const totalCost = costData.total_cost;
  const feedback = summary?.feedback || {};

  // Sorted agent table
  const sortedTable = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortField] || SORT_ACCESSORS.cost;
    const rows = [...agentTable];
    rows.sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av === bv) return 0;
      const cmp = av < bv ? -1 : 1;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [agentTable, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'agent_name' || field === 'has_evals' ? 'asc' : 'desc');
    }
  };

  // ── Permission gates ────────────────────────────────────────────────
  if (isPermissionsLoading) {
    return (
      <div className="genai-evals-dark" style={{ textAlign: 'center', padding: '60px 34px' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p style={{ marginTop: 12, color: '#a4b0cc' }}>Checking permissions...</p>
      </div>
    );
  }

  if (!canAccessAiGovernance()) {
    return (
      <div style={{ padding: '40px 34px' }}>
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Access Denied</h4>
          <p className="mb-1">Your role does not have access to AI Governance.</p>
          <p className="mb-0">
            <strong>Current role:</strong> {getUserRole()}
          </p>
        </div>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="genai-evals-dark">
      {/* Header */}
      <div
        style={{
          padding: '24px 0',
          borderBottom: '1px solid #2a3550',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 8,
        }}
      >
        <div>
          <h4 style={{ margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: '-0.2px' }}>
            AI Governance Report
            {meta?.environment && (
              <span
                style={{
                  display: 'inline-block',
                  padding: '3px 11px',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  background: '#4f8cff',
                  color: '#fff',
                  marginLeft: 8,
                  verticalAlign: 'middle',
                }}
              >
                {meta.environment}
              </span>
            )}
          </h4>
          <p style={{ color: '#a4b0cc', fontSize: 14, marginTop: 6, marginBottom: 0 }}>
            Agent evaluation, cost, and safety overview across all GenAI agents.
          </p>
        </div>
        <div style={{ textAlign: 'right', color: '#a4b0cc', fontSize: 13.5 }}>
          {generatedAt && (
            <div>
              Generated <b style={{ color: '#eef2fb', fontWeight: 600 }}>{generatedAt}</b>
            </div>
          )}
          {source && (
            <div>
              Source: <b style={{ color: '#eef2fb', fontWeight: 600 }}>{source}</b>
            </div>
          )}
        </div>
      </div>

      {/* Tabs (window selector) + Refresh */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', paddingTop: 18 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {windowOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setTimeWindow(opt.value)}
              style={{
                padding: '11px 22px',
                border: '1px solid #2a3550',
                borderBottom: 'none',
                borderRadius: '10px 10px 0 0',
                background: timeWindow === opt.value ? '#1e2740' : '#171e2e',
                color: timeWindow === opt.value ? '#eef2fb' : '#a4b0cc',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 15,
                boxShadow: timeWindow === opt.value ? 'inset 0 3px 0 #4f8cff' : 'none',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="ge-btn"
          onClick={() => fetchReport({ forceRefresh: true })}
          disabled={isLoading}
        >
          <i className="bi bi-arrow-clockwise me-1" aria-hidden="true" />
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between mt-3" role="alert">
          <span>{error}</span>
          <button className="btn btn-sm btn-outline-danger" onClick={() => fetchReport()}>
            Retry
          </button>
        </div>
      )}

      {/* Loading skeleton */}
      {isLoading && !reportData && (
        <div className="card mt-3">
          <div style={{ padding: 24 }}>
            <div className="placeholder-glow">
              <span className="placeholder col-3 mb-2" />
              <span className="placeholder col-12" style={{ height: 80 }} />
            </div>
          </div>
        </div>
      )}

      {/* ── Main content ────────────────────────────────────────────── */}
      {!isLoading && !error && reportData && (
        <>
          {/* ═══ Executive Summary KPIs ═══ */}
          <h2 className="ge-section">Executive summary</h2>

          <div className="ge-kpis">
            <KpiCard label="Agents Deployed" value={agentsDeployed} />
            <KpiCard
              label="Users"
              value={feedback.has_feedback ? feedback.unique_users ?? 'N/A' : 'N/A'}
              variant="users"
            />
            <KpiCard label="Total Cost" value={usd(totalCost)} variant="cost" />
          </div>

          {/* ═══ Agent scorecards table ═══ */}
          <div className="card">
            <h3>Agents</h3>
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <SortableHeader label="Agent" field="agent_name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader label="Agent Pattern" field="agent_pattern" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader
                      label="Evals"
                      field="has_evals"
                      sortField={sortField}
                      sortDir={sortDir}
                      onSort={handleSort}
                      className="text-center"
                    />
                    <SortableHeader label="Accuracy" field="accuracy" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="text-end" />
                    <SortableHeader label="Users" field="unique_users" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="text-end" />
                    <SortableHeader label="Avg Risk Score" field="avg_risk_score" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="text-end" />
                    <SortableHeader label="Sessions Scanned" field="scanned" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="text-end" />
                    <SortableHeader label="Token Usage" field="tokens" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="text-end" />
                    <SortableHeader label="Cost" field="cost" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="text-end" />
                  </tr>
                </thead>
                <tbody>
                  {sortedTable.length === 0 && (
                    <tr>
                      <td colSpan={9} className="ge-empty">
                        No agent data available for this window.
                      </td>
                    </tr>
                  )}
                  {sortedTable.map((row) => (
                    <tr
                      key={row.agent_id}
                      role="button"
                      onClick={() => navigate(`/genai-evals/agents/${row.agent_id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="ge-agent-name">{row.agent_name || row.agent_id}</div>
                      </td>
                      <td>{fmtPattern(row.agent_pattern)}</td>
                      <td className="text-center">
                        {row.has_evals ? (
                          <i className="bi bi-check-circle-fill text-success" title="Yes" />
                        ) : (
                          <i className="bi bi-x-circle" style={{ color: '#a4b0cc' }} title="No" />
                        )}
                      </td>
                      <td className="text-end">
                        <span className={`ge-score-num ${ragClass(row.accuracy)}`}>
                          {row.accuracy != null ? pct(row.accuracy) : 'N/A'}
                        </span>
                      </td>
                      <td className="text-end">{row.unique_users ?? 'N/A'}</td>
                      <td className="text-end">{fmtRisk(row.avg_risk_score)}</td>
                      <td className="text-end">{row.scanned ?? 'N/A'}</td>
                      <td className="text-end">{tokfmt(row.tokens)}</td>
                      <td className="text-end">{usd(row.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Footer ────────────────────────────────────────────────── */}
          {generatedAt && (
            <div
              style={{
                color: '#a4b0cc',
                fontSize: 13,
                padding: '24px 0',
                borderTop: '1px solid #2a3550',
                marginTop: 20,
              }}
            >
              Report generated at {generatedAt}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AiGovernance;
